import { useState } from "react";
import { api, errMsg, PRIORITIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CustomerPicker } from "@/components/CustomerPicker";
import { X } from "lucide-react";

const EMPTY = { customer_name: "", customer_phone: "", title: "", description: "", sector_id: "none", queue_id: "none", os_type_id: "none", assigned_to: "none", priority: "Normal" };

const Pick = ({ label, value, onChange, items, none, testId }) => (
  <div><Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="none">{none}</SelectItem>{items.map((s) => <SelectItem key={s.id || s} value={s.id || s}>{s.name || s}</SelectItem>)}</SelectContent>
    </Select></div>
);

export function NewOrderDialog({ open, onOpenChange, cat, onCreated }) {
  const { hasPerm } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const act = { queues: cat.queues.filter((q) => q.status !== "Inativa"), sectors: cat.sectors.filter((s) => s.active !== false), types: cat.types.filter((t) => t.active !== false) };
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState(false);

  const pickCustomer = (c) => { setCustomer(c); setNewCustomer(false); setForm((f) => ({ ...f, customer_name: c?.name || "", customer_phone: c?.phone || "" })); };
  const startNew = () => { setCustomer(null); setNewCustomer(true); setForm((f) => ({ ...f, customer_name: "", customer_phone: "" })); };
  const reset = () => { setForm(EMPTY); setCustomer(null); setNewCustomer(false); };

  const submit = async () => {
    if (!customer && !newCustomer) return toast.error("Selecione um cliente cadastrado ou cadastre um novo");
    if (!form.customer_name || !form.customer_phone || !form.title) return toast.error("Preencha nome, telefone e assunto");
    setLoading(true);
    try {
      const payload = { ...form };
      ["sector_id", "queue_id", "os_type_id", "assigned_to"].forEach((k) => { if (payload[k] === "none") payload[k] = null; });
      await api.post("/orders", payload);
      toast.success("OS criada com sucesso"); onOpenChange(false); reset(); onCreated();
    } catch (e) { toast.error(errMsg(e, "Erro ao criar OS")); } finally { setLoading(false); }
  };
  const onQueue = (v) => { const q = act.queues.find((x) => x.id === v); setForm({ ...form, queue_id: v, sector_id: q?.sector_id || form.sector_id }); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white dark:bg-slate-900 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nova Ordem de Serviço</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!newCustomer && <CustomerPicker value={customer} onSelect={pickCustomer} onNew={startNew} />}
          {newCustomer && (
            <div className="p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 space-y-3" data-testid="new-customer-form">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Cliente novo</p>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setNewCustomer(false); setForm((f) => ({ ...f, customer_name: "", customer_phone: "" })); }} data-testid="cancel-new-customer-btn"><X className="w-4 h-4 mr-1" /> Buscar cadastrado</Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Nome do cliente *</Label><Input data-testid="os-customer-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
                <div><Label>Telefone (com DDI) *</Label><Input data-testid="os-customer-phone-input" placeholder="5511999999999" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
              </div>
            </div>
          )}
          <div><Label>Assunto</Label><Input data-testid="os-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><Label>Descrição</Label><Textarea data-testid="os-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Pick label="Fila" value={form.queue_id} onChange={onQueue} items={act.queues} none="Sem fila" testId="os-queue-select" />
            <Pick label="Tipo de OS" value={form.os_type_id} onChange={(v) => setForm({ ...form, os_type_id: v })} items={act.types} none="Sem tipo" testId="os-type-select" />
            <Pick label="Setor" value={form.sector_id} onChange={(v) => setForm({ ...form, sector_id: v })} items={act.sectors} none="Sem setor" testId="os-sector-select" />
            <div><Label>Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="os-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            {hasPerm("assign_os") && <div className="col-span-2"><Pick label="Responsável (opcional — vazio usa distribuição da fila)" value={form.assigned_to} onChange={(v) => setForm({ ...form, assigned_to: v })} items={cat.users} none="Automático / não atribuído" testId="os-assignee-select" /></div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-os-btn">{loading ? "Criando..." : "Criar OS"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
